import * as React from "react";
import { Form } from "formik";

import { IItemTag, IEditForm, ITagWithGroup } from "../types";
import { withUser } from "src/services/withUser";
import { isLocalContentAdmin } from "src/services/authorizeUser";
import {
  FormInput,
  Button,
  FormCheckbox,
  FormSelect,
  FormObjectList,
  TooltipIcon,
  Icons,
  FormTextarea,
  WarningBlock,
} from "src/components";
import ItemSelector from "../../components/ItemSelector";

const illegalChars = ["#", "&", "%", "$", "§", '"', "!", "(", ")", "/"];

const sameTagAndItem = (item: IItemTag) => (tag: ITagWithGroup) =>
  tag.tagValue === item.tagValue && tag.tagGroup.id === item.tagGroup.id;

const EditForm: React.FC<IEditForm> = ({
  id,
  itemOptionsWithParameter,
  parameterOptions,
  marketOptions,
  tagGroupOptions,
  values,
  errors,
  isSubmitting,
  children,
  isValid,
  dirty,
  tagValidation,
  withWarnings,
  onRemove,
  onToggleArchive,
  isArchive,
  user,
}) => {
  const selectedParameter = id
    ? values["parameter-" + id]
    : values["parameter-new"];
  const itemName = id ? values["name-" + id] : values["name-new"];

  // check parameter regex validation
  let parameterValidationError = false;
  if (selectedParameter && selectedParameter.validation) {
    if (!itemName.match(selectedParameter.validation)) {
      // regex not matched
      parameterValidationError = true;
    }
  }

  // check illegal chars
  const specialCharsUsed = itemName
    ? illegalChars.some((x) => itemName.includes(x))
    : false;

  const hasTagErrors = tagValidation.errors.length > 0;
  const isLocalAdmin = isLocalContentAdmin(user.roles);
  const isLocalAdminAndEdit = id !== "new" && isLocalAdmin;
  const validationErrorMessages = Object.values(errors)
    .map((value) =>
      typeof value === "object" ? Object.values(value)[0] : value
    )
    .join(", ");

  return (
    <div className="pt-5 px-6 mx-px">
      <Form autoComplete="off">
        {children}
        <div className="flex">
          <div className="flex-1">
            <div className="flex mb-4">
              <span className="pt-2 pr-6">Parameter*</span>
              <div className="flex-1">
                <FormSelect
                  className="flex-1"
                  name={`parameter-${id}`}
                  options={parameterOptions}
                  initialValue={{ label: "", value: "" }}
                  isDisabled={!parameterOptions.length || isLocalAdminAndEdit}
                />
              </div>
            </div>
          </div>
          <div className="flex-1 ml-8">
            <div className="flex mb-4">
              <label className="pt-2 pr-6" htmlFor={`name-${id}`}>
                Name*
              </label>
              <div className="flex-1">
                <FormInput
                  className="w-full"
                  name={`name-${id}`}
                  disabled={isLocalAdminAndEdit}
                />
              </div>
            </div>
            <div className="flex mb-4">
              <WarningBlock
                left={true}
                hidden={!specialCharsUsed && !parameterValidationError}
                classNames="min-w-full"
              >
                <p>
                  <span className={specialCharsUsed ? "" : "hidden"}>
                    It is not recommended to use special characters reserved for
                    urls ({illegalChars.join(",")}).{" "}
                  </span>
                  <span
                    className={
                      specialCharsUsed && parameterValidationError
                        ? ""
                        : "hidden"
                    }
                  >
                    <br />
                  </span>
                  <span className={parameterValidationError ? "" : "hidden"}>
                    Validation for parameter failed:{" "}
                    {selectedParameter ? selectedParameter.msgError : ""}
                  </span>
                  <span
                    className={
                      specialCharsUsed || parameterValidationError
                        ? ""
                        : "hidden"
                    }
                  >
                    <br />
                    This is just a warning, you can still save this item.
                  </span>
                </p>
              </WarningBlock>
            </div>
          </div>
        </div>

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <FormObjectList
            name={`itemTags-${id}`}
            addLabel="Add tag"
            addDisabled={tagGroupOptions.length === 0 || isLocalAdminAndEdit}
            removeDisabled={isLocalAdminAndEdit}
            addObject={{
              tagValue: "",
              tagGroup: tagGroupOptions[0] && tagGroupOptions[0].value,
            }}
          >
            {({ name, item }) => {
              const addOption =
                tagGroupOptions.findIndex(
                  ({ value }) => value.id === item.tagGroup.id
                ) === -1;
              const sameTag = sameTagAndItem(item);
              const tagWarning = tagValidation.warnings.find(sameTag);
              const tagErrors = tagValidation.errors.filter(sameTag);
              const hasDuplicateError =
                tagErrors.length > 0 &&
                tagErrors.findIndex((tag) => tag.type === "duplicate") !== -1;
              const hasValidationError =
                tagErrors.length > 0 &&
                tagErrors.findIndex((tag) => tag.type === "validation") !== -1;

              return (
                <>
                  <FormSelect
                    className="w-48 mr-2"
                    name={`${name}.tagGroup`}
                    options={
                      addOption
                        ? [
                            {
                              label: item.tagGroup.name,
                              value: {
                                name: item.tagGroup.name,
                                id: item.tagGroup.id,
                              },
                            },
                            ...tagGroupOptions,
                          ]
                        : tagGroupOptions
                    }
                    isDisabled={isLocalAdminAndEdit}
                  />
                  <FormInput
                    className="flex-1 mr-2"
                    name={`${name}.tagValue`}
                    disabled={isLocalAdminAndEdit}
                  />
                  {tagWarning && (
                    <TooltipIcon
                      className="mx-2"
                      type="warning"
                      width="w-64"
                      text={`Warning: ${
                        tagWarning.type === "duplicateGroup"
                          ? "The tag group is duplicated."
                          : "The value you have entered already exists."
                      }`}
                    />
                  )}
                  {(hasDuplicateError || hasValidationError) && (
                    <TooltipIcon
                      className="mx-2"
                      type="error"
                      width="w-64"
                      text={`Error: ${
                        hasDuplicateError
                          ? "The value you have entered already exists. Please enter another value."
                          : ""
                      } ${
                        hasValidationError
                          ? "The value you have entered does not match the requested format for this tag. Please enter another value."
                          : ""
                      }`}
                    />
                  )}
                </>
              );
            }}
          </FormObjectList>
        </div>

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <div className="flex mb-4">
            <label className="w-48 pr-6" htmlFor={`defaultProfiles-${id}`}>
              Visibility Market
            </label>
            <FormCheckbox
              name={`defaultProfiles-${id}`}
              disabled={isLocalAdmin}
            >
              Show by default in all Market
            </FormCheckbox>
          </div>
          <div className="flex mb-4">
            <p className="w-48 pt-2 pr-6">Exceptions</p>
            <div className="flex-1">
              <FormObjectList
                name={`itemProfileVisibilityConditions-${id}`}
                addLabel="Add exception"
                addObject={{
                  profile: marketOptions[0] && marketOptions[0].value,
                  visible: true,
                }}
              >
                {({ name }) => (
                  <>
                    <FormSelect
                      className="flex-1"
                      name={`${name}.profile`}
                      options={marketOptions}
                    />
                    <FormCheckbox
                      className="w-32 ml-6"
                      name={`${name}.visible`}
                      disabled={isLocalAdminAndEdit}
                    >
                      Visible
                    </FormCheckbox>
                  </>
                )}
              </FormObjectList>
            </div>
          </div>
        </div>

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <div className="flex mb-4">
            <label className="w-48 pr-6" htmlFor={`defaultItems-${id}`}>
              Visibility Item
            </label>
            <FormCheckbox
              name={`defaultItems-${id}`}
              disabled={isLocalAdminAndEdit}
            >
              Show by default
            </FormCheckbox>
          </div>
          <div className="flex mb-4">
            <p className="w-48 pt-2 pr-6">Exceptions</p>
            <div className="flex-1">
              <FormObjectList
                name={`itemVisibilityConditions-${id}`}
                addLabel="Add exception"
                addObject={{
                  itemSource: {},
                  visible: true,
                }}
                addDisabled={isLocalAdminAndEdit}
                removeDisabled={isLocalAdminAndEdit}
              >
                {({ name, item }) => (
                  <>
                    <ItemSelector
                      name={`${name}.itemSource`}
                      parameterOptions={parameterOptions}
                      itemOptionsWithParameter={itemOptionsWithParameter}
                      selectedItemId={item.itemSource && item.itemSource.id}
                      disabled={
                        !itemOptionsWithParameter.length || isLocalAdminAndEdit
                      }
                    />
                    <FormCheckbox
                      className="w-32 ml-6"
                      name={`${name}.visible`}
                      disabled={isLocalAdminAndEdit}
                    >
                      Visible
                    </FormCheckbox>
                  </>
                )}
              </FormObjectList>
            </div>
          </div>
        </div>

        <hr className="my-6 border-b border-grey-dark" />

        <div>
          <div className="flex mb-4">
            <label
              className="w-48 pt-2 pr-6"
              htmlFor={`linkCreationNotification-${id}`}
            >
              Notification on Link Created page
            </label>
            <div className="flex-1">
              <FormTextarea
                className="w-full h-32"
                name={`linkCreationNotification-${id}`}
                disabled={isLocalAdminAndEdit}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center pt-4 pb-5 bg-grey-transparent sticky pin-b pin-r">
          {onToggleArchive && !isLocalAdminAndEdit && (
            <Button
              onClick={onToggleArchive}
              type="button"
              size="small"
              background="grey"
              neutral={true}
            >
              {isArchive ? (
                <>
                  <Icons.Circle character="↺" className="mr-2" /> Restore
                </>
              ) : (
                <>
                  <Icons.Circle character="×" className="mr-2" /> Archive
                </>
              )}
            </Button>
          )}
          {/* {onRemove && (
            TODO: Permanent remove
            <Button
              onClick={onRemove}
              type="button"
              size="small"
              background="grey"
              error={true}
            >
              <Icons.Trash size={16} className="mr-2" /> Delete
            </Button>
          )} */}
          <div className="flex-1 pr-4 text-right text-red">
            {hasTagErrors ? "Fix tag errors before saving. " : ""}
            {validationErrorMessages}
          </div>
          <Button
            type="submit"
            background="grey"
            loading={isSubmitting}
            loadingLabel="Saving"
            disabled={hasTagErrors || !dirty || !isValid}
          >
            Save {withWarnings ? "with warnings" : ""}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default withUser(EditForm);



// WEBPACK FOOTER //
// ./src/views/Admin/Items/components/EditForm.tsx